import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { assign, map as lodashMap } from 'lodash';
import { forkJoin, from, map } from 'rxjs';
import { CardColumnSelectionBase } from 'src/app/classes/base-classes/card-column/card-column-selection-base';
import { Company, CompanyOffice, Vendor } from 'src/app/classes/models';
import { ArraySelectOption } from 'src/app/interfaces/array-select-option';
import { Card } from 'src/app/interfaces/card';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { VendorService } from 'src/app/services/vendor.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

import { CardColumnDisplayModalComponent } from '../card-column-modal/card-column-display-modal/card-column-display-modal.component';

@Component({
  selector: 'app-company-offices-modal',
  templateUrl:
    '../card-column-modal/card-column-display-modal/card-column-display-modal.component.html',
  styleUrls: [
    '../card-column-modal/card-column-accordion-display-modal/card-column-accordion-display-modal.component.less',
    './company-offices-modal.component.less'
  ],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CompanyOfficesModalComponent
  extends CardColumnDisplayModalComponent
  implements OnInit
{
  constructor(
    protected override activeModal: NgbActiveModal,
    protected override ToastService: ToastService,
    protected override ChangeDetectorRef: ChangeDetectorRef,
    protected override SharedDataService: SharedDataService,
    private VendorService: VendorService,
    private AuthService: AuthService,

    // I want this modal to be used by both Clients and Vendors. However, a Vendor requires a Client Id
    // when using this modal, whereas a Client does NOT require a Vendor Id. EXTEND THIS COMPONENT FROM
    // THE (currently non-existent) CompanyOfficeModal.ts. (This component will become CompanyOfficesSelectionModal)
    private CompanyService: CompanyService
  ) {
    super(activeModal, ToastService, ChangeDetectorRef, SharedDataService);
  }

  // IMPORTANT NOTE BELOW
  // This is really only for Vendors during office selection. Thus, this component should be extended from
  // the CompanyOffices modal (this modal) and this modal should be renamed CompanyOfficesSelectionModal.
  @Input() selectedCompanyOffices!: CompanyOffice[];

  @Input() VendorId: number | null = null;
  @Input() CompanyIds: number[] = [];

  Companies: Company[] = [];

  override ngOnInit(): void {
    if (!Array.isArray(this.CompanyIds) || this.CompanyIds.length === 0) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    const observables = {};

    this.AuthService.getUserType().subscribe({
      next: (userType) => {
        if (['Admin', 'Vendor'].includes(userType)) {
          if (!this.VendorId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing VendorId.',
              { returnError: true }
            );
          }
          assign(observables, {
            Companies: this.VendorService.getVendorV2(this.VendorId, {
              attributes: JSON.stringify(['id']),
              CompanyVendor: JSON.stringify({
                attributes: ['id', 'CompanyId', 'VendorId'],
                CompanyId: { $in: this.CompanyIds },
                Company: {
                  attributes: ['id', 'legalName'],
                  CompanyOffice: {}
                }
              })
            }).pipe(
              map((Vendor: Vendor) => {
                if (!Vendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor information.',
                    { returnError: true }
                  );
                }
                const { CompanyVendors } = Vendor;
                if (
                  !Array.isArray(CompanyVendors) ||
                  CompanyVendors.length === 0
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor information.',
                    { returnError: true }
                  );
                }

                return CompanyVendors.map((cv) => {
                  const { Company } = cv;
                  if (!Company) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Client information.',
                      { returnError: true }
                    );
                  }
                  return Company;
                });
              })
            )
          });
        } else if (['Client'].includes(userType)) {
          if (!Array.isArray(this.CompanyIds) || this.CompanyIds.length !== 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client Id.',
              { returnError: true }
            );
          }

          const CompanyId = this.CompanyIds[0];
          assign(observables, {
            Companies: from(
              companiesService.findOneById({
                pathParams: {
                  companyId: CompanyId
                },
                queryParams: {
                  includeAssociations: [
                    {
                      name: 'CompanyOffices'
                    }
                  ]
                }
              })
            ).pipe(
              map((Company) => {
                if (!Company) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client information.',
                    { returnError: true }
                  );
                }
                return [Company];
              })
            )
          });
        } else {
          throw this.ToastService.addErrorToast('Error', 'Invalid permission', {
            returnError: true
          });
        }
      }
    });

    forkJoin(observables).subscribe({
      next: ({ Companies }: { Companies: Company[] }) => {
        if (!Array.isArray(Companies) || Companies.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        this.Companies.push(...Companies);

        const clientLegalNames: string[] = [];
        const isNoOffices = this.Companies.every((Company) => {
          const { legalName, CompanyOffices } = Company;

          if (!legalName) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client legal name.',
              { returnError: true }
            );
          }

          if (Array.isArray(CompanyOffices) && CompanyOffices.length > 0) {
            clientLegalNames.push(legalName);

            this.cardCollection = lodashMap(CompanyOffices, (CompanyOffice) => {
              return {
                bodyText: CompanyOffice.officeName,
                record: {
                  value: CompanyOffice,
                  comparator: (compareValue, selectedValue) =>
                    compareValue.id === selectedValue.id
                } as ArraySelectOption<CompanyOffice>
              } as Card;
            });

            return false;
          }

          return true;
        });

        if (isNoOffices) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Client has any offices.',
            { returnError: true }
          );
        }

        this.enableFiltering = true;
        this.title = `Select the offices you are willing to service for ${clientLegalNames.join(', ')}`;
        this.message = `Please select office(s) that your business wishes to service or that is closest to the location your business currently services. Multiple offices may be selected.`;

        this.selectionModel = new CardColumnSelectionBase(
          'multiple',
          this.selectedCompanyOffices
        );

        super.ngOnInit();
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }
}
